
  import { defineComponent, inject, markRaw } from "vue";
  import { setOptions, importLibrary } from "@googlemaps/js-api-loader";
  import { get, set } from "lodash";

  import BaseProcessComponentV2 from "@/components/processComponents/v2/BaseProcessComponentV2.vue";
  import { latitude, longitude } from "@/helpers/customValidators";
  import { required } from "@/helpers/i18n-validators";

  const defaultCenter = { lat: 14.0917822, lng: -87.2025082 };

  export default defineComponent({
    name: "MapV2",

    extends: BaseProcessComponentV2,

    setup(props: any) {
      const root: string = props.path.split(".")[0];

      return {
        [root]: inject(root),
      };
    },

    data() {
      return {
        defaultValue: {} as any,
        internalValue: {} as any,
        map: null as google.maps.Map | null,
        marker: null as google.maps.marker.AdvancedMarkerElement | null,
        advancedMarkerElementClass: null as typeof google.maps.marker.AdvancedMarkerElement | null,
      };
    },

    validations() {
      let validations = {};
      const [root, ...rest] = this.path.split(".");

      set(validations, rest.join("."), {
        [this.latKey]: { latitude, required },
        [this.lngKey]: { longitude, required },
      });

      return { [root]: validations };
    },

    async mounted() {
      await (BaseProcessComponentV2 as any).mounted?.call(this);

      this.defaultValue = { [this.latKey]: null, [this.lngKey]: null };
      this.internalValue = { [this.latKey]: null, [this.lngKey]: null };

      (this.$refs[this.ref] as HTMLElement).style.height = get(this, "settings.attributes.height", "400px") as string;

      if (!this.value?.[this.latKey] || !this.value?.[this.lngKey]) {
        this.value = { [this.latKey]: null, [this.lngKey]: null };
      }

      setOptions({ key: this.settings.mapConfig.apiKey });

      const { Map } = (await importLibrary("maps")) as google.maps.MapsLibrary;
      const { AdvancedMarkerElement } = (await importLibrary("marker")) as google.maps.MarkerLibrary;

      this.advancedMarkerElementClass = AdvancedMarkerElement;

      const mapElement = this.$refs[this.ref] as HTMLElement;

      const mapInstance = new Map(mapElement, {
        zoom: get(this, "settings.mapConfig.zoom", 13) as number,
        mapId: get(this, "settings.mapConfig.id", "") as string,
        clickableIcons: false,
        cameraControl: get(this, "settings.mapConfig.cameraControl", true) as boolean,
        streetViewControl: get(this, "settings.mapConfig.streetViewControl", true) as boolean,
        fullscreenControl: get(this, "settings.mapConfig.fullscreenControl", true) as boolean,
      });

      this.map = markRaw(mapInstance);

      if (!this.value?.[this.latKey] || !this.value?.[this.lngKey]) {
        const coords: any = await this.getUserLocation();
        this.map.setCenter(coords as any);
        this.value = { [this.latKey]: coords.lat, [this.lngKey]: coords.lng };
      } else {
        this.map.setCenter({ lat: this.value[this.latKey], lng: this.value[this.lngKey] });
      }

      if (!this.isReadOnly) {
        this.map.addListener("click", (event: google.maps.MapMouseEvent) => {
          const lat = event.latLng!.lat();
          const lng = event.latLng!.lng();
          this.value = { [this.latKey]: lat, [this.lngKey]: lng };
          this.placeMarker({ lat, lng });
        });
      }

      if (this.value[this.latKey] !== null && this.value[this.lngKey] !== null) {
        await this.placeMarker({ lat: this.value[this.latKey], lng: this.value[this.lngKey] });
      }
    },

    computed: {
      latKey(): string {
        return get(this, "settings.attributes.latKey", "lat") as string;
      },

      lngKey(): string {
        return get(this, "settings.attributes.lngKey", "lng") as string;
      },

      validationState() {
        return {
          lat: get(this.v$, `${this.path}.${this.latKey}`),
          lng: get(this.v$, `${this.path}.${this.lngKey}`),
        };
      },
    },

    methods: {
      getUserLocation() {
        return new Promise((resolve) => {
          if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
              (position) => {
                resolve({
                  lat: position.coords.latitude,
                  lng: position.coords.longitude,
                });
              },
              () => {
                resolve(get(this, "settings.mapConfig.center", defaultCenter));
              },
              {
                enableHighAccuracy: true,
                timeout: 5000,
                maximumAge: 0,
              }
            );
          } else {
            resolve(get(this, "settings.mapConfig.center", defaultCenter));
          }
        });
      },

      async placeMarker(coords: { lat: number; lng: number }) {
        if (this.advancedMarkerElementClass && this.map) {
          if (this.marker) {
            this.marker.position = coords;
          } else {
            this.marker = markRaw(
              new this.advancedMarkerElementClass!({
                map: this.map,
                position: coords,
              })
            );
          }

          this.map.panTo(this.marker.position as any);
        }
      },
    },

    watch: {
      value: {
        handler(newValue: Record<string, number>) {
          if (newValue[this.latKey] !== null && newValue[this.lngKey] !== null) {
            this.placeMarker({ lat: newValue[this.latKey], lng: newValue[this.lngKey] });
          }
        },
        deep: true,
      },
    },
  });
